import { IEntity } from '../interfaces/entity';
import { ProcessType } from './process-type';
import { Subject } from './subject';
import { CurrencyPipe } from '@angular/common';
import { LookupTypeService } from '../services/lookup-type.service';
import { LookupService } from '../services/lookup.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const PROCESSFIXEDFIELDS = ['summary', 'linkedsubject'];

export interface IProcessList {
  items: Process[];
  totalCount: number;
}

export class Process implements IEntity {
  id: number;
  uid: string;
  reference: string;
  summary: string;
  subjectId: number;
  processTypeId: number;
  processData: ProcessData;
  canDelete: boolean;
  archived: boolean;
  subject: Subject;
  organisationId: number;
  dateTimeCompleted: Date;
  processType:ProcessType=null;

  constructor(process?) {
    process = process || {};
    this.id = process.id;
    this.uid = process.uid;
    this.reference = process.reference || '';
    this.summary = process.summary || '';
    this.subjectId = process.subjectId || '';
    this.dateTimeCompleted = process.dateTimeCompleted;
    this.processType = process.processType;
    this.processTypeId = process.processTypeId;
    this.processData = process.processData || new ProcessData();
    this.processData.processId = process.id;
    this.processData.processTypeId = process.processTypeId;
    if (process.processData) {
      this.processData.data = process.processData.data;
      this.processData.displayValues = process.processData.displayValues;
    }
    this.canDelete = process.canDelete;
    this.archived = process.archived;
    this.subject = process.subject;
    this.organisationId = process.organisationId;
  }

  getSummary(processType: ProcessType, forceReCalc?: boolean) {
    // check if we already have a summary tracker defined and which matches the current processtype and category
    if ((forceReCalc === true) || (!this.summary)) {
      // either summary tracker not instantiated, or the processtype doesn't match - re-create summary tracker by calling getMetadata
      // we need to do this the long winded way as we need the calculated display values which are also retrieved during getMetadata
      // we could stash the display values, but we would still need to call getMetadata, so let's just go with stashing the bits we need for now :).
      this.getMetadata(processType);
    }
    return this.summary || this.reference;
  }

  getMetadata(processType: ProcessType): any[] {
    const result = [];

    // no fixed fields other than summary - but if we do have some at a later stage then we need to process them here first...

    // check we have process data fields...
    if (!this.processData.data) {
      let s = '{';
      processType.dataFields.forEach((item) => {
        if (PROCESSFIXEDFIELDS.indexOf(item.name.toLowerCase()) < 0) {
          s += `"${item.id}":null,`;
        }
      });
      if (s.length > 1) {
        s = s.slice(0, -1);
      }
      this.processData.data = JSON.parse(s + '}');
      // if we didn't have data then we certainly won't have corresponding displayValues...
      this.processData.displayValues = this.processData.data;
    }

    // now iterate over process data fields - the process type gives us the possible fields
    processType.dataFields.forEach((item) => {
      if (PROCESSFIXEDFIELDS.indexOf(item.name.toLowerCase()) < 0) {
        const itemData = this.processData.data[item.id];        
        if (itemData || (itemData === false) || (itemData === 0) || (itemData=="") ) {
          const val = this.processData.displayValues[item.id];
          let displayValue;
          switch (item.dataType.toLowerCase()) {
            case 'currency':
              displayValue = new CurrencyPipe('en-GB').transform(val, 'GBP', 'symbol', '1.2');
              break;
            case 'date':
              displayValue = dayjs.utc(val).add(2, 'hours').format('DD/MMM/YYYY');
              break;
            case 'yesno':
              if (val.toLocaleLowerCase()==="true") displayValue = "Yes";
              else if (val.toLocaleLowerCase()==="false") displayValue = "No";
              else displayValue = " - ";
              break;
            default:
              if (itemData!==0) displayValue = val;
              break;
          }
          result.push({ id: item.id, value: itemData, displayValue, type: item.dataType.toLowerCase(), label: item.label });
        }
      }
    });

    // ...and finally build the summary specified by the passed layout
    let summary = '';
    if (processType.summary) {
      const tags = JSON.parse(processType.summary);
      if (Array.isArray(tags) && (tags.length > 0)) {
        tags.forEach((item) => {
          // search the values array for a matching entry
          let val = result.find((x) => x.id.toString() === item.name.toString());
          if (val) {
            // result array has a matching entry, include the display value in the summary
            summary = summary + val.displayValue;
          }
          else {
            // if we get here there ie either no value for the field, or the suummary tag is not a field...
            val = processType.dataFields.find((x) => x.id.toString() === item.name.toString());
            if (!val) {
              // not a field, so add the label to the summary
              summary = summary + item.label;
            }
          }
        });
      }
      if (!summary) {
        // no summary tags found, so use reference
        summary = this.reference;
      }
      // now stash the summary for future reference
      this.summary = summary;

      // ...and push onto our stack of results to return
      result.push({ id: processType.getFieldId('summary'), value: summary, displayValue: summary });
    }
    // console.log('Results:', result);
    return result;
  }

  // set the process data fields - the process type gives us the possible fields
  setFromMetadata(processType: ProcessType, metadata: any[]) {
    processType.dataFields.forEach((item) => {
      if (PROCESSFIXEDFIELDS.indexOf(item.name.toLowerCase()) < 0) {
        const itemData = this.processData.data[item.id];
        const index = metadata.findIndex((meta) => meta.id === item.id);
        if ((index > -1) || (itemData || (itemData === false) || (itemData === 0))) {
          // existing value or incoming value specified, set the processData accordingly
          this.processData.data[item.id] = (index < 0) ? itemData : metadata[index].value;
        }
      }
    });

    // process any calculated fields
    processType.dataFields.filter((i) => i.dataType.toLowerCase() === 'calculated').forEach((item) => {
      const field1 = (item.field1 === '0') ? undefined : processType.dataFields.find((i) => i.uid === item.field1);
      const field2 = (item.field2 === '0') ? undefined : processType.dataFields.find((i) => i.uid === item.field2);
      const val1 = (field1) ? this.processData.data[field1.id] : item.value1;
      const val2 = (field2) ? this.processData.data[field2.id] : item.value2;
      if ((val1 || (val1 === 0)) && (val2 || (val2 === 0))) {
        let result = 0;
        switch (item.operator.toLowerCase()) {
          case 'divide': result = ((val1 !== 0) && (val2 !== 0)) ? (val1 / val2) : 0; break;
          case 'minus': result = val1 - val2; break;
          case 'multiply': result = val1 * val2; break;
          case 'plus': result = val1 + val2; break;
        }
        const num = Number((Math.abs(result) * 100).toPrecision(15));
        this.processData.data[item.id] = Math.round(num) / 100 * Math.sign(result);
      }
      else {
        this.processData.data[item.id] = undefined;
      }
    });
  }

  addData(processType: ProcessType, fieldName: string, value: any) {
    if (processType && fieldName) {
      const field = processType.dataFields.find((i) => i.name.toLowerCase() === fieldName.toLowerCase());
      if (field) {
        if (!this.processData.data) {
          this.processData.data = {};
        }
        this.processData.data[field.id] = value;
      }
    }
  }

  addDisplayValue(processType: ProcessType, fieldName: string, value: any) {
    if (processType && fieldName) {
      const field = processType.dataFields.find((i) => i.name.toLowerCase() === fieldName.toLowerCase());
      if (field) {
        if (!this.processData.displayValues) {
          this.processData.displayValues = {};
        }
        this.processData.displayValues[field.id] = value;
      }
    }
  }

  addDefaultOrganisation(subject: Subject, processType: ProcessType, lookupService: LookupService, lookupTypeService: LookupTypeService) {    
    // Add default organisation.          
    if (subject.subjectOrganisations && subject.subjectOrganisations.length > 0) {
      var org = subject.subjectOrganisations[0];
      if (org) {
        // Look in process type's data fields for field with Organisation in name.
        var hasOrganisationField = processType.dataFields.find(x => x.name.toLocaleLowerCase().indexOf("organisation") > -1);
        if (hasOrganisationField) {
          // This code retrieves a cache of lookup types, finds a specific lookup type based on a 
          // process type's data field which has the word "organisation" in its name, retrieves all lookups for that type, finds a 
          // specific lookup matching a given organisation name, and adds the lookup ID and description to 
          // a data field of a process type if the field exists. 
          const cache = lookupTypeService.getCache();
          const PartnerOrganisationLookup = cache.find((x) => (x.id == parseInt(hasOrganisationField.dataType)));
          if (PartnerOrganisationLookup) {
            lookupService.getAllByLookupType(PartnerOrganisationLookup.id).subscribe((lookups) => {
              var orgLookup = lookups.find((x) => {
                return (x.description.toLocaleLowerCase() == org.organisation.name.toLocaleLowerCase());
              });
              if (orgLookup) {
                const field = processType.dataFields.find((i) => i.name.toLowerCase().includes("organisation"));
                if (field) {
                  this.addData(processType, field.name, orgLookup.id);
                  this.addDisplayValue(processType, field.name, orgLookup.description);
                }
              }
            });
          }
        }
      }
    }
  }
}

// tslint:disable-next-line:max-classes-per-file
export class ProcessData implements IEntity {
  id: number;
  uid: string;
  processId: number;
  processTypeId: number;
  data: any;
  // separate property to mirror the data property, but replacing any lookup ids with lookup descriptions
  // we are using a separate property because if we modify the data property we are going to have to clean it all up prior to saving/update
  // and that is going to get complicated!!!
  displayValues: any;
}
